import { useQueryParams } from 'use-query-params'
import React, { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { LntButton, LntDropdown } from '@app/components/base'
import { ContentLayout } from '@app/layouts'
import { ExtendedTable, PermissionWrapper, SubheaderToolbarDivider, TableSettingsToolbar } from '@app/components'
import { RemoveListModal } from '@app/modals/RemoveListModal'
import { CopyQuotaModal } from '@app/modals/CopyQuotaModal'
import { Icon, Show } from '@app/components/util'
import { CommonQuotaDrawer } from '@app/drawers'
import { exportQuotasFile, exportQuotaFile } from '@app/utils/export/exportFile'
import { getExportFilesDropdownMenu, isEmptyArray } from '@app/utils'

import { FILE_TYPES } from '@app/api/constants'
import { BUTTON_TYPE, SIZE } from '@app/constants'
import { QUOTAS_REPORTS_KEY } from '@app/constants/queryKeys'
import { TABLE_SETTING_KEYS } from '@app/constants/tableSettingKeys'

import { useNotification, usePaginationQueryParams, useTableSearch, useTableSetting, useToggle } from '@app/hooks'

import { copyQuota, deleteQuota, patchQuota, getQuotaDirectories } from '@app/api/routes/quotaReports'

import { PERMISSION } from '@app/constants/permission'

import { ICON_NAME } from '@app/constants/icon'

import { TopFilters } from './ui'
import { topFiltersQuerySchema, columnsSearchSchema, tableSearchQuerySchema } from './configs'
import { getColumns, adaptQuotaReportDirectoryTableData, adaptGetQuotaParams } from './lib'

import type { Key } from 'react'
import type { FiltersParams } from '@app/types/filter'
import type { CopyQuotaData, GetQuotaParams, PatchQuotaData, CreateQuotaData } from '@app/api/routes/quotaReports/types'
import type { FileType } from '@app/types'

const columns = getColumns()

export const DirectoryTab: React.FC = () => {
  const { paginationParams, pagination, setPagination, resetCurrentPage } = usePaginationQueryParams()

  const notification = useNotification()

  const [queryFilters, setQueryFilters] = useQueryParams(topFiltersQuerySchema)

  const queryClient = useQueryClient()

  const quotasDataParams = useMemo(
    () => ({ ...paginationParams, ...queryFilters }),
    [paginationParams, queryFilters]
  ) as GetQuotaParams

  const { data: quotaReportDirectoryTableData, isLoading: isQuotasDataLoading } = useQuery({
    queryKey: [QUOTAS_REPORTS_KEY.quotaReportItems, quotasDataParams],
    queryFn: async () => await getQuotaDirectories(quotasDataParams),
    select: quotaDirectoriesData => adaptQuotaReportDirectoryTableData(quotaDirectoriesData)
  })

  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const removeQuotaModalToggle = useToggle()
  const editQuotaDrawerToggle = useToggle()
  const copyQuotaModalToggle = useToggle()

  const buttonLoadingExportToggle = useToggle()
  const buttonLoadingExportAllToggle = useToggle()

  const deleteQuotaMutation = useMutation({
    mutationFn: deleteQuota,
    onSuccess: async () => {
      notification.success({
        message: 'Успешно:',
        description: 'Список успешно удален!'
      })

      await queryClient.invalidateQueries({ queryKey: [QUOTAS_REPORTS_KEY.quotaReportItems] })
    }
  })

  const handleDeleteQuota = (): void => {
    if (isEmptyArray(selectedIds)) return

    deleteQuotaMutation.mutate({ ids: selectedIds }, { onSettled: removeQuotaModalToggle.setUntoggled })
  }

  const editQuotaMutation = useMutation({
    mutationFn: patchQuota,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QUOTAS_REPORTS_KEY.quotaReportItems] })

      notification.success({
        message: 'Успешно:',
        description: 'Квота успешно изменена'
      })
    }
  })

  const handleEditQuota = (data: CreateQuotaData): void => {
    if (!selectedIds.length) return
    const id = String(selectedIds[0])

    const patchData: PatchQuotaData = {
      id,
      brandCount: data.brandCount,
      eppCount: data.eppCount
    }

    editQuotaMutation.mutate(patchData, { onSettled: editQuotaDrawerToggle.setUntoggled })
  }

  const copyQuotaMutation = useMutation({
    mutationFn: copyQuota,
    onSuccess: async () => await queryClient.invalidateQueries({ queryKey: [QUOTAS_REPORTS_KEY.quotaReportItems] })
  })

  const handleCopyQuota = (data: CopyQuotaData): void => {
    if (!selectedIds.length) return

    const id = String(selectedIds[0])

    copyQuotaMutation.mutate({ ...data, id }, { onSettled: copyQuotaModalToggle.setUntoggled })
  }

  const selectedProduct = useMemo(
    () => quotaReportDirectoryTableData?.dataSource?.find(({ key }) => String(key) === selectedIds[0]),
    [selectedIds]
  )

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handleFiltersChange = (filters: FiltersParams): void => {
    setQueryFilters(filters)
    resetCurrentPage()
  }

  const handleSelectedRowChange = (selectedKeys: Key[]): void => {
    setSelectedIds(selectedKeys.map(String))
  }

  const handleExportAllQuotasClick = async (fileType: FileType): Promise<void> => {
    buttonLoadingExportAllToggle.setToggled()

    const getQuotaParams = adaptGetQuotaParams(queryFilters)

    const data = {
      ...getQuotaParams,
      file: fileType
    }

    const fileName = 'Список_квот'

    try {
      await exportQuotasFile(fileName, data)
    } finally {
      buttonLoadingExportAllToggle.setUntoggled()
    }
  }

  const handleExportQuotaClick = async (fileType: FileType): Promise<void> => {
    buttonLoadingExportToggle.setToggled()

    const fileName = 'Квота'

    try {
      await exportQuotaFile(fileName, { ids: selectedIds, file: fileType })
    } finally {
      buttonLoadingExportToggle.setUntoggled()
    }
  }

  const {
    items,
    defaultItems,
    columns: modifiedColumns,
    onModify
  } = useTableSetting(TABLE_SETTING_KEYS.directoryTab, columns)

  const {
    columns: columnsWithSearchTool,
    filteredDataSource: filteredQuotasDirectoriesDataSource,
    isSearchToolOpened,
    toggleSearchTool
  } = useTableSearch({
    columns: modifiedColumns,
    dataSource: quotaReportDirectoryTableData?.dataSource,
    columnsSearchSchema,
    tableSearchQuerySchema
  })

  const exportDropdownItems = getExportFilesDropdownMenu(
    'Квота',
    [FILE_TYPES.xlsx, FILE_TYPES.csv],
    handleExportQuotaClick
  )
  const exportAllDropdownItems = getExportFilesDropdownMenu(
    'Список квот',
    [FILE_TYPES.xlsx, FILE_TYPES.csv],
    handleExportAllQuotasClick
  )

  const toolbar = [
    <Show
      key='change_export-show'
      condition={ selectedIds.length === 1 }
    >
      <PermissionWrapper requiredPermissions={ [PERMISSION.editQuotas] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ removeQuotaModalToggle.setToggled }
        >
          Удалить
        </LntButton>
      </PermissionWrapper>

      <PermissionWrapper requiredPermissions={ [PERMISSION.editQuotas] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ copyQuotaModalToggle.setToggled }
        >
          Копировать
        </LntButton>
      </PermissionWrapper>

      <PermissionWrapper requiredPermissions={ [PERMISSION.editQuotas] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ editQuotaDrawerToggle.setToggled }
        >
          Редактировать
        </LntButton>
      </PermissionWrapper>

      <PermissionWrapper requiredPermissions={ [PERMISSION.viewQuotas] }>
        <LntDropdown
          trigger={ ['click'] }
          items={ exportDropdownItems }
        >
          <LntButton
            type={ BUTTON_TYPE.text }
            size={ SIZE.small }
            loading={ buttonLoadingExportToggle.value }
          >
            Экспорт
            <Icon name={ ICON_NAME.arrowDown } />
          </LntButton>
        </LntDropdown>
      </PermissionWrapper>

      <SubheaderToolbarDivider />
    </Show>,

    <PermissionWrapper
      key='export_all-button'
      requiredPermissions={ [PERMISSION.viewQuotas] }
    >
      <LntDropdown
        trigger={ ['click'] }
        items={ exportAllDropdownItems }
      >
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          loading={ buttonLoadingExportAllToggle.value }
        >
          Экспорт всех
          <Icon name={ ICON_NAME.arrowDown } />
        </LntButton>
      </LntDropdown>

      <SubheaderToolbarDivider />
    </PermissionWrapper>,

    <TableSettingsToolbar
      key='table-settings-toolbar'
      tableSettingButtonProps={ { items, defaultItems, onAccept: onModify } }
      tableSearchButtonProps={ { active: isSearchToolOpened, onClick: toggleSearchTool } }
    />
  ]

  return (
    <ContentLayout>
      <TopFilters
        onSearch={ handleFiltersChange }
        initialFilters={ queryFilters as FiltersParams }
      />

      <ExtendedTable
        loading={ isQuotasDataLoading }
        columns={ columnsWithSearchTool }
        dataSource={ filteredQuotasDirectoriesDataSource }
        toolbar={ toolbar }
        rowSelection={ {
          onChange: handleSelectedRowChange
        } }
        pagination={ {
          ...pagination,
          total: quotaReportDirectoryTableData?.count,
          onChange: handlePaginationChange
        } }
      />

      <RemoveListModal
        open={ removeQuotaModalToggle.value }
        onCancel={ removeQuotaModalToggle.setUntoggled }
        onRemoveList={ handleDeleteQuota }
        title='Вы действительно хотите удалить выбранные позиции?'
      />

      <CommonQuotaDrawer
        open={ editQuotaDrawerToggle.value }
        dataSource={ selectedProduct }
        title='Редактирование квоты'
        onClose={ editQuotaDrawerToggle.setUntoggled }
        onSubmit={ handleEditQuota }
        isEditForm
      />

      <CopyQuotaModal
        open={ copyQuotaModalToggle.value }
        onCancel={ copyQuotaModalToggle.setUntoggled }
        onCreateQuota={ handleCopyQuota }
      />
    </ContentLayout>
  )
}
